import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { FOCUS_BORDER } from "../../config/focusStyles";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType, getCMSImageURL } from "../../utils/cms.utils";
import ContentTag from "../../utils/tag";
import classNames from "classnames";
import { getAgeRangePublicRoute } from "../../utils/ageRange.util";
import ViewMoreCard from "../common/ViewMoreCard";
import { useInputMode } from "../../utils/hooks/useInputMode";
import OptimizedImage from "../common/OptimizedImage";

// ============================================================================
// CONSTANTS
// ============================================================================

var aspectGuide = {
  movie: "w-[250px] h-[350px]",
  searchbox: "w-[250px] h-[350px]",
  series: "w-[250px] h-[350px]",
  program: "h-[350px] w-[620px]",
  sport: "h-[350px] w-[620px]",
  music: "w-[250px] h-[250px]",
  animation: "w-[250px] h-[250px]",
  artist: "w-[215px] h-[215px]",
};

// ============================================================================
// STYLED COMPONENTS (Legacy CSS compatible)
// ============================================================================

var ContentRowScrollingWrapper = styled.div`
  -ms-flex-negative: 1;
  flex-shrink: 1;
  -webkit-box-flex: 1;
  -ms-flex-positive: 1;
  flex-grow: 1;
  padding-left: 60px;
  -webkit-transition: padding ease 0.2s;
  transition: padding ease 0.2s;
  overflow-x: auto;
  padding-left: 5rem;
`;

var ContentRowScrollingContent = styled.div`
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-transition: all ease 0.5s;
  transition: all ease 0.5s;
  width: -webkit-fit-content;
  width: -moz-fit-content;
  width: fit-content;
  position: relative;
`;

var ContentRowWrapper = styled.div`
  position: relative;
  min-height: 400px;
  background-color: ${function (props) {
    return props.$kind === "music" ? "#121212" : "transparent";
  }};
  border-radius: ${function (props) {
    return props.$kind === "music" ? "20px" : "0";
  }};
  width: ${function (props) {
    return props.$kind === "music" ? "calc(100% - 5rem)" : "100%";
  }};
  margin: ${function (props) {
    return props.$kind === "music" ? "2rem auto" : "2rem 0";
  }};
  &:after {
    content: "";
    position: absolute;
    pointer-events: none;
    width: 312px;
    height: 100%;
    background: ${function (props) {
    return props.$kind !== "music"
      ? "linear-gradient(to right, #121212, rgba(0, 0, 0, 0))"
      : "none";
  }};
    left: 0;
    top: 0;
  }
`;

var ContentRowTitle = styled.div`
  color: white;
  margin-bottom: 22px;
  font-size: 27px;
  font-weight: 700;
  padding-right: 40px;
  padding-top: 40px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  width: 100%;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: space-between;
  z-index: 999;
  position: relative;
  font-family: "AnjomanMax", serif;
`;

var ItemWrapper = styled.div`
  position: relative;
  margin-left: 0.75rem;
  padding-top: 1.25rem;
`;

var SeriesDecoration = styled.div`
  position: absolute;
  bottom: 0;
  top: -1rem;
  right: 5px;
  left: 5px;
  height: 100%;
`;

var SeriesImage = styled.img`
  width: 100%;
  margin-top: 1rem;
`;

var CardContainer = styled.div`
  min-width: 215px;
  border-radius: 15px;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  margin-bottom: 5%;
  position: relative;
  z-index: ${function (props) {
    return props.$focused ? 10 : 0;
  }};
  border: ${function (props) {
    return props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive;
  }};
  margin: 2px;
  cursor: pointer;
`;

var CardImage = styled.img`
  width: 100%;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
`;

var FocusOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${function (props) {
    return props.$focused ? 1 : 0;
  }};
  border-radius: 11px;
  pointer-events: none;
  -webkit-transition: opacity 0.2s;
  transition: opacity 0.2s;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  gap: 1rem;
  z-index: 10;
`;

var ItemTitle = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  font-family: "AnjomanMax", serif;
`;

var ItemYear = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  font-family: "AnjomanMax", serif;
`;

var ArtistName = styled.span`
  color: white;
  font-weight: 700;
  margin-top: 0.25rem;
  margin-right: 0.25rem;
  font-family: "AnjomanMax", serif;
`;

var ViewMoreWrapper = styled.div`
  padding-top: 1.25rem;
`;

// ============================================================================
// COMPONENTS
// ============================================================================

var MovieItem = function (props) {
  var item = props.item || {};
  var onFocus = props.onFocus;
  var shapeType = props.shapeType;

  var navigate = useNavigate();
  var contentPhotos = item.contentPhotos;
  var title = item.title;
  var year = item.year;
  var slug = item.slug;
  var tags = item.tags;
  var image = item.image;
  var farsiName = item.farsiName;
  var kind = item.kind;
  var contentType = item.contentType;

  var handleNavigate = function () {
    if (shapeType === "artist") {
      navigate("/artists/" + slug);
    } else {
      var contentKind = contentType?.kind || kind || "movie";
      navigate("/" + contentKind + "/" + slug);
    }
  };

  var focusableResult = useFocusable({
    onFocus: onFocus,
    onEnterPress: handleNavigate,
  });

  var focused = focusableResult.focused;
  var ref = focusableResult.ref;
  var focusSelf = focusableResult.focusSelf;

  var inputModeResult = useInputMode();
  var setMouseFocus = inputModeResult.setMouseFocus;
  var handleMouseEnter = useCallback(function () {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  var getImage = function () {
    if (shapeType === "artist") {
      return { src: getCMSImageURL(image) };
    } else {
      return getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 90, format: 'jpeg' });
    }
  };

  var isSeries = contentType?.kind === "series" || kind === "series";
  var imageData = getImage();

  return (
    <ItemWrapper>
      {isSeries && (
        <SeriesDecoration>
          <SeriesImage src="/serial_pseudo.svg" alt="" />
        </SeriesDecoration>
      )}
      <CardContainer
        ref={ref}
        $focused={focused}
        onMouseEnter={handleMouseEnter}
        onClick={handleNavigate}
        className={classNames("cursor-pointer", aspectGuide[shapeType])}
      >
        {typeof imageData === 'string' ? (
          <CardImage src={imageData} alt={title} />
        ) : (
          <OptimizedImage
            src={imageData.src}
            alt={title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
        {tags?.[0] && <ContentTag label={tags[0]} />}
        <FocusOverlay $focused={focused}>
          <ItemTitle>{title}</ItemTitle>
          <ItemYear>{year}</ItemYear>
        </FocusOverlay>
      </CardContainer>
      {farsiName && <ArtistName>{farsiName}</ArtistName>}
    </ItemWrapper>
  );
};

export var MoviesSlider = function (props) {
  var movies = props.movies;
  var onFocus = props.onFocus;
  var rowTitle = props.rowTitle;
  var shapeType = props.shapeType || "movie";
  var id = props.id;
  var index = props.index;
  var actionLink = props.actionLink || null;

  var ageRange = getAgeRangePublicRoute();
  var _items = movies.filter(function (x) {
    return (
      x.content?.ageRange <= ageRange ||
      x.contentMeta?.content?.ageRange <= ageRange ||
      x.ageRange <= ageRange
    );
  });

  var focusableResult = useFocusable({
    onFocus: onFocus,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusKey: "movie_slider_" + id,
    focusable: movies.length > 0,
  });

  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;

  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];

  var navigate = useNavigate();
  var scrollingRef = useRef(null);
  var innerRef = useRef(null);

  // Show View More card when items > 12
  var showViewMore = _items.length > 12;

  var inputModeResult = useInputMode();
  var isMouseFocus = inputModeResult.isMouseFocus;
  var onAssetFocus = useCallback(
    function (focusProps) {
      // Skip scroll adjustment on mouse focus
      if (isMouseFocus) return;
      var left = focusProps.left;
      if (left < 200) {
        var offset =
          shapeType === "sport" ? Math.abs(left - 500) : Math.abs(left - 300);
        innerRef.current.style.transform = "translateX(" + offset + "px)";
      } else {
        innerRef.current.style.transform = "translateX(0px)";
      }
    },
    [shapeType, isMouseFocus]
  );

  var paddingOffsetCount = shapeType === "sport" ? 2 : 5;

  if (_items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref} $kind={shapeType} tabIndex={index}>
        <ContentRowTitle>{rowTitle}</ContentRowTitle>
        <ContentRowScrollingWrapper
          ref={scrollingRef}
          style={
            selected > paddingOffsetCount
              ? { paddingRight: 0 }
              : { paddingRight: 40 }
          }
        >
          <ContentRowScrollingContent ref={innerRef}>
            {_items?.map(function (item, itemIndex) {
              return (
                <MovieItem
                  item={item}
                  key={"ms_" + item?.slug + "_" + id + "_" + itemIndex}
                  onFocus={function (e) {
                    setSelected(itemIndex);
                    onAssetFocus(e);
                  }}
                  shapeType={shapeType}
                />
              );
            })}
            {/* View More Card - shown at end when items > 12 */}
            {showViewMore && (
              <ViewMoreWrapper>
                <ViewMoreCard
                  actionLink={actionLink || "category;" + id}
                  shapeType={shapeType}
                  focusKey={"view_more_" + id}
                  onFocus={function (e) {
                    setSelected(_items.length);
                    onAssetFocus(e);
                  }}
                />
              </ViewMoreWrapper>
            )}
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
